import React from "react";
import { Loader, TextLink } from "@src/components/atoms";
import Tooltip from "@src/components/atoms/Tooltip";
import RecentEventCard from "@src/components/cards/operator/RecentEventCard";
import BestEventCard from "@src/components/cards/operator/BestEventCard";
import { useNavigate } from "react-router-dom";
import { useDCContext } from "@src/context/dcContext";
import { useOperatorContext } from "@src/context/operatorContext";
import OperatorHooks from "@src/context/hooks/operatorHooks";
import SavingsOrCharityCard from "@src/components/cards/operator/SavingsOrCharityCard";
import { convertFuelBurnedToCo2 } from "@signolteam/shared/utils";
import { formatMass, formatNumber } from "@signolteam/shared/units-of-measure";
import SavingsCard from "@src/components/cards/SavingsCard";
import { SignolUtils } from "../../../utils";

const AllTimeHighlights = () => {
  const { profile, setSelectedEvent } = useOperatorContext();
  const dc = useDCContext();
  const navigate = useNavigate();
  const { customer } = profile;
  const { existsDynamicReplacements, getDynamicReplacement } = dc;
  const industry = customer.industry;

  //Best event
  const {
    data: { bestEvent } = {},
    isPending: bestEventPending,
    error: bestEventError,
  } = OperatorHooks.useOperatorBestEvent(profile?.operator?.id);
  //Impact
  const {
    data: { impact, personalAllTimeTotals } = {},
    isPending: impactPending,
    error: impactError,
  } = OperatorHooks.useOperatorImpact(profile?.operator?.id);

  const charity = profile.charities.find(
    (c) => c.id == profile.operator.charityId,
  );
  const isDonations =
    charity != null && personalAllTimeTotals?.donations?.["TOTAL"] > 1
      ? new Date().getHours() % 1 == 0 //maximum every other hour
      : false;

  const navigateToEvents = (event) => {
    setSelectedEvent(event);
    navigate(`/dashboard/operator/events/${event.id}`);
  };

  return (
    <div className="flex flex-col items-center mb-6 md:mb-0">
      <h3 className="font-extrabold mb-4">All-Time Highlights</h3>
      <div className="flex flex-col gap-3">
        {bestEventPending ? (
          <div className="min-h-20">
            <div className="w-full h-full flex items-center justify-center">
              <Loader size="xl" />
            </div>
          </div>
        ) : bestEventError ? (
          <div>Error fetching best event...</div>
        ) : (
          !!bestEvent && (
            <BestEventCard
              industry={industry}
              bestEvent={bestEvent}
              getDynamicContent={dc.getDynamicReplacement}
              handleClick={() => navigateToEvents(bestEvent)}
              trackingActionKey="OVERVIEW_BEST_EVENT_SELECTION"
            />
          )
        )}
        {/* SavingsOrCharity card */}
        {impactPending ? (
          <div className="min-h-20">
            <div className="w-full h-full flex items-center justify-center">
              <Loader size="xl" />
            </div>
          </div>
        ) : impactError ? (
          <div>Error fetching impact data...</div>
        ) : !!impact ? (
          <div
            className="w-full mb-3"
            {...SignolUtils.makeTrackableDataAttributes({
              actionKey: isDonations
                ? "OVERVIEW_DONATION_TOTAL_CARD_CLICK"
                : "OVERVIEW_CARBON_SAVINGS_TOTAL_CARD_CLICK",
            })}
          >
            <SavingsOrCharityCard
              industry={industry}
              text={
                !isDonations
                  ? "Your CO₂ savings:"
                  : existsDynamicReplacements(
                        `charities__${customer.customerCode.toLowerCase()}__overview_donations_card_text`,
                      )
                    ? getDynamicReplacement(
                        `charities__${customer.customerCode.toLowerCase()}__overview_donations_card_text`,
                        {
                          DONATION_TOTAL: formatNumber(
                            personalAllTimeTotals.donations["TOTAL"],
                          ),
                        },
                      )
                    : getDynamicReplacement(
                        "charities__default__overview_donations_card_text",
                        {
                          DONATION_TOTAL: formatNumber(
                            personalAllTimeTotals.donations["TOTAL"],
                          ),
                        },
                      )
              }
              total={
                !!isDonations
                  ? null
                  : formatMass(
                      industry,
                      convertFuelBurnedToCo2(
                        personalAllTimeTotals.savings["TOTAL"],
                        1,
                      ),
                    ).formattedValue
              }
              imgUrl={
                !!isDonations
                  ? getDynamicReplacement(
                      existsDynamicReplacements(
                        `charities__${customer.customerCode.toLowerCase()}__small_logo`,
                      )
                        ? `charities__${customer.customerCode.toLowerCase()}__small_logo`
                        : `captain__events__behaviour__swiper__donations_icon`,
                    )
                  : getDynamicReplacement(
                      "captain__events__behaviour__swiper__co2_icon",
                    )
              }
              getDynamicContent={dc.getDynamicReplacement}
              handleClick={() =>
                navigate(
                  `/dashboard/operator/impact#${isDonations ? "donations" : "carbon"}`,
                )
              }
            />
          </div>
        ) : (
          <SavingsCard
            value={0}
            industry={industry}
            customerCode={customer.customerCode}
          />
        )}{" "}
      </div>

      <TextLink
        side="right"
        to="/dashboard/operator/impact"
        onClick={() => navigate("/dashboard/operator/impact")}
        text="See your impact"
        trackingActionKey="MENU_SELECTION"
      />
    </div>
  );
};

export default AllTimeHighlights;
